import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Logo } from '../components/Logo'
import { AuthLayout } from '../components/AuthLayout'
import { Button, Field, Input } from '../components/ui'
import { GoogleGlyph } from '../components/icons'
import { PasswordRequirements } from '../components/PasswordRequirements'
import { useCaptcha } from '../lib/useCaptcha'
import { SIGNED_OUT_NOTE_KEY } from '../lib/api'
import { PASSWORD_MIN_LENGTH, passwordMeetsPolicy, friendlyAuthError } from '../lib/password'

export default function Login() {
  const [params] = useSearchParams()
  // Landing "Start" CTAs deep-link here as /login?mode=signup; default is sign-in.
  const [mode, setMode] = useState<'signin' | 'signup'>(params.get('mode') === 'signup' ? 'signup' : 'signin')
  // Invitation emails deep-link here as /login?email=<address> (or ?mode=signup&email=), so the address the
  // invite was sent to is already filled in and the person signs in or creates their account with it.
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [err, setErr] = useState<string | null>(null)
  // A session ended on the server (lib/api resyncSession) brings the person here: say why, once.
  const [notice, setNotice] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SIGNED_OUT_NOTE_KEY) ? 'You were signed out on this device. Please sign in again.' : null
    } catch {
      return null
    }
  })
  useEffect(() => {
    try {
      sessionStorage.removeItem(SIGNED_OUT_NOTE_KEY)
    } catch {
      /* nothing to clear */
    }
  }, [])
  const [awaitingConfirm, setAwaitingConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const cap = useCaptcha()
  const nav = useNavigate()

  const CONFIRM_REDIRECT = `${location.origin}/auth/confirm`

  /** Re-send the confirmation email after a sign-up. Never reveals whether the address already has an account. */
  async function resendConfirm() {
    setErr(null)
    const captchaToken = (await cap.waitForToken()) || undefined
    const { error } = await supabase.auth.resend({ type: 'signup', email, options: { emailRedirectTo: CONFIRM_REDIRECT, captchaToken } })
    setNotice(error ? null : 'Sent again. Check your inbox.')
    if (error) {
      cap.reset()
      setErr(friendlyAuthError(error))
    }
  }

  /** Email a link to set a new password — also how a Google-only account adds one. Never reveals
   *  whether the email has an account. */
  async function sendReset() {
    setErr(null)
    setNotice(null)
    if (!email) return setErr('Enter your email above first.')
    const captchaToken = (await cap.waitForToken()) || undefined
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${location.origin}/reset-password`, captchaToken })
    if (error) {
      cap.reset()
      return setErr(friendlyAuthError(error))
    }
    setNotice('If that email has an account, a link to set a new password is on its way.')
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setNotice(null)
    // Warn before we ever call Supabase when a new password can't pass the policy (the checklist
    // below shows which rule is missing). Sign-in is exempt so existing shorter passwords still work.
    if (mode === 'signup' && !passwordMeetsPolicy(password)) {
      return setErr('Your password needs to meet all the requirements below.')
    }
    setBusy(true)
    try {
      // Wait for the captcha token (on a slow link the widget needs a moment) so we never submit without one.
      const captchaToken = (await cap.waitForToken()) || undefined
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: name }, emailRedirectTo: CONFIRM_REDIRECT, captchaToken },
        })
        if (error) throw error
        if (!data.session) {
          // Confirmation required: Supabase sent the code/link (and sends nothing, revealing nothing, when
          // the address already has an account). Show the check-your-email step with resend, not a sign-in flip.
          setAwaitingConfirm(true)
          return
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password, options: { captchaToken } })
        if (error) throw error
      }
      nav('/app')
    } catch (e) {
      cap.reset() // the token is single-use; get a fresh one for the retry
      setErr(friendlyAuthError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      eyebrow="The agent's home base"
      headline="Collect tapes, track every audition, keep clients in the loop."
      points={['Unified casting inbox', 'Self-tapes kept forever', 'Live status for your clients']}
    >
      <Logo className="text-lg md:hidden" />
      <h1 className="mt-6 font-display text-3xl md:mt-0">
        {mode === 'signin' ? 'Welcome back' : 'Create your account'}
      </h1>
      <p className="mt-2 text-[color:rgb(var(--muted))]">
        {mode === 'signin' ? 'Sign in to your RepTree workspace.' : 'Start your free month, no card required.'}
      </p>
      {awaitingConfirm ? (
        <div className="mt-6 space-y-3">
          <p className="text-sm">
            We sent a confirmation to <strong>{email}</strong>. Open it and enter the code, or click the button in it, to finish.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Button type="button" onClick={() => void resendConfirm()} className="justify-center">Resend</Button>
            <button type="button" onClick={() => { setAwaitingConfirm(false); setErr(null) }} className="label-mono text-[color:rgb(var(--muted))] hover:text-[color:rgb(var(--fg))]">
              Use a different email
            </button>
          </div>
          <p className="text-sm text-[color:rgb(var(--muted))]">
            No email after a minute? You may already have an account:{' '}
            <button type="button" onClick={() => { setAwaitingConfirm(false); setMode('signin') }} className="underline">sign in</button>{' '}
            or <button type="button" onClick={() => void sendReset()} className="underline">reset your password</button>.
          </p>
          {notice && <p className="text-sm text-emerald">{notice}</p>}
          {err && <p role="alert" className="text-sm text-clay">{err}</p>}
        </div>
      ) : (
      <>
      <form onSubmit={submit} className="mt-6 space-y-4">
        {mode === 'signup' && (
          <Field label="Your name">
            <Input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
          </Field>
        )}
        <Field label="Email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={mode === 'signup' ? PASSWORD_MIN_LENGTH : undefined}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
          />
        </Field>
        {mode === 'signup' && <PasswordRequirements password={password} className="mt-2" />}
        {mode === 'signin' && (
          <button type="button" onClick={() => void sendReset()} className="label-mono text-[color:rgb(var(--muted))] hover:text-[color:rgb(var(--fg))]">
            Forgot password?
          </button>
        )}
        {notice && <p className="text-sm text-emerald">{notice}</p>}
        {err && <p role="alert" className="text-sm text-clay">{err}</p>}
        {cap.widget}
        <Button type="submit" disabled={busy} className="w-full justify-center">
          {busy ? '…' : mode === 'signin' ? 'Sign in' : 'Create account'}
        </Button>
      </form>

      <div className="mt-4 flex items-center gap-3" aria-hidden>
        <span className="hairline h-px flex-1 border-t" />
        <span className="label-mono text-[color:rgb(var(--muted))]">or</span>
        <span className="hairline h-px flex-1 border-t" />
      </div>
      <button
        type="button"
        onClick={async () => {
          setErr(null)
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: `${location.origin}/app` },
          })
          if (error) setErr(error.message)
        }}
        className="hairline mt-4 flex w-full items-center justify-center gap-3 rounded-card border bg-[color:rgb(var(--bg-elev))] px-4 py-2.5 text-sm transition hover:bg-[color:rgb(var(--tint-1))]"
      >
        <GoogleGlyph />
        Continue with Google
      </button>
      <button
        onClick={() => {
          setMode((m) => (m === 'signin' ? 'signup' : 'signin'))
          setErr(null)
        }}
        className="label-mono mt-5 text-[color:rgb(var(--muted))] hover:text-[color:rgb(var(--fg))]"
      >
        {mode === 'signin' ? 'New here? Create an account' : 'Already have an account? Sign in'}
      </button>
      </>
      )}
    </AuthLayout>
  )
}
